import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { ANNUAL_MASK_BATCHES_COLLECTION, ANNUAL_MASK_CASES_COLLECTION, getAnnualMaskRoleList } from "./annualMaskPurchaseUtils";

const text = (value) => String(value || "").trim();
export const isAnnualMaskSale = (user) => getAnnualMaskRoleList(user).includes("sale");
export const getAnnualMaskAllowedNodes = (profile = {}, nodes = []) => {
  const codes = (Array.isArray(profile.node) ? profile.node : [profile.node]).map(text).filter(Boolean);
  return Array.from(new Set(nodes.filter((node) => codes.includes(text(node.nodecode)))
    .flatMap((node) => [text(node.nodecode), text(node.nodename)]).filter(Boolean)));
};
export const isAnnualMaskCaseInNodes = (caseItem = {}, allowed = []) => {
  const patient = caseItem.patient || {};
  return allowed.includes(text(caseItem.nodeRef || patient.nodeRef || caseItem.node || patient.node));
};
export const loadAnnualMaskAllowedNodes = async (database, user) => {
  const name = text(user && user.displayName);
  if (!name) return [];
  const [profile, nodes] = await Promise.all([getDoc(doc(database, "admins", name)), getDocs(collection(database, "nodes"))]);
  return profile.exists() ? getAnnualMaskAllowedNodes(profile.data(), nodes.docs.map((node) => node.data())) : [];
};
const nodeQueries = (casesRef, allowed) => {
  const queries = [];
  for (let index = 0; index < allowed.length; index += 10) {
    const values = allowed.slice(index, index + 10);
    ["nodeRef", "patient.nodeRef", "node", "patient.node"].forEach((field) => queries.push(query(casesRef, where(field, "in", values))));
  }
  return queries;
};
export const loadAnnualMaskSaleCaseDocs = async (casesRef, allowed = []) => {
  const snapshots = await Promise.all(nodeQueries(casesRef, allowed).map((request) => getDocs(request)));
  return Array.from(new Map(snapshots.flatMap((snapshot) => snapshot.docs).map((row) => [row.id, row])).values())
    .filter((row) => isAnnualMaskCaseInNodes(row.data(), allowed));
};

// Notifications use the same node scope, and rebuild listeners when an
// administrator changes the salesperson's assigned nodes.
export const subscribeAnnualMaskSaleCases = (database, user, onCases, onError) => {
  let active = true;
  let failed = false;
  let profile = null;
  let nodes = null;
  let batches = null;
  let stops = [];
  let generation = 0;
  const clear = () => { generation += 1; stops.forEach((stop) => stop()); stops = []; };
  const rebuild = () => {
    if (!active || failed) return;
    clear();
    onCases([]);
    if (!profile || !nodes || !batches) return;
    const allowed = getAnnualMaskAllowedNodes(profile, nodes);
    if (!allowed.length) return;
    const currentGeneration = generation;
    const results = new Map();
    const requests = batches.flatMap((batch) => nodeQueries(collection(database, ANNUAL_MASK_BATCHES_COLLECTION, batch.id, ANNUAL_MASK_CASES_COLLECTION), allowed).map((request, index) => ({ request, key: `${batch.id}:${index}`, batch })));
    requests.forEach(({ request, key, batch }) => {
      stops.push(onSnapshot(request, (snapshot) => {
        if (!active || currentGeneration !== generation) return;
        results.set(key, snapshot.docs.map((row) => ({ ...row.data(), id: row.id, refPath: row.ref.path, batchId: batch.id, batchName: row.data().batchName || batch.batchName || "" })));
        if (results.size !== requests.length) return;
        onCases(Array.from(new Map(Array.from(results.values()).flat().map((row) => [row.refPath, row])).values()).filter((row) => isAnnualMaskCaseInNodes(row, allowed)));
      }, (error) => {
        if (!active || currentGeneration !== generation) return;
        failed = true; clear(); onCases([]); onError(error);
      }));
    });
  };
  const fail = (error) => { if (active) { failed = true; clear(); onCases([]); onError(error); } };
  const name = text(user && user.displayName);
  if (!name) { onCases([]); return () => {}; }
  const roots = [
    onSnapshot(doc(database, "admins", name), (snapshot) => { profile = snapshot.exists() ? snapshot.data() : {}; rebuild(); }, fail),
    onSnapshot(collection(database, "nodes"), (snapshot) => { nodes = snapshot.docs.map((row) => row.data()); rebuild(); }, fail),
    onSnapshot(collection(database, ANNUAL_MASK_BATCHES_COLLECTION), (snapshot) => { batches = snapshot.docs.map((row) => ({ ...row.data(), id: row.id })); rebuild(); }, fail),
  ];
  return () => { active = false; clear(); roots.forEach((stop) => stop()); };
};
